import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyInput,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ROLE_LABELS } from "@shared/permissions";
import type { MeInstance } from "@shared/types";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { usePermissions } from "@/hooks/use-permissions";
import { instanceCanOpen, useReturnTo } from "@/hooks/use-return-to";
import { addressIn } from "@/lib/instancePath";
import { useAuth } from "@/lib/auth/AuthContext";
import { failureMessage } from "@/lib/api";
import { AdminIcon, ArrowRightIcon, RetryIcon, SearchIcon, SignOutIcon } from "@/lib/icons";
import { firstAllowedPath, firstPlatformPath } from "@/lib/routes";

/**
 * The instance picker (UX-6, FR-20).
 *
 * Every other screen redirects here until an instance is chosen, because a
 * connection, a run and an audit row all belong to one instance and there is no
 * sensible "all instances" view of them.
 *
 * The exception is platform administration (FR-20). Tenants,
 * instances, connector definitions, platform settings and the platform audit
 * trail belong to the DEPLOYMENT, not to an instance, so this screen offers them
 * as a way out rather than a row in the list. Without that, a fresh deployment
 * has no instances, and the only person who can create the first one is told to
 * ask an administrator for the access they already hold.
 *
 * Two things the canvas asks for and the list needs at scale: instances are
 * GROUPED BY TENANT whenever the user holds access in more than one, because
 * "prod" exists in every tenant and the instance name alone identifies nothing;
 * and a search box appears once the list outgrows a glance, filtering instance
 * and tenant names together so either half of the identity finds the row.
 */

/** Above this many instances the list stops being scannable and gets a search box. */
const SEARCH_THRESHOLD = 8;

function PickerSkeleton() {
  return (
    <SkySkeleton testId="instances-loading">
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((row) => (
          <div key={row} className="rounded-lg border border-border p-4">
            <SkySkeletonItem width={180} />
            <div className="mt-2">
              <SkySkeletonItem width={260} size={10} />
            </div>
          </div>
        ))}
      </div>
    </SkySkeleton>
  );
}

/**
 * One group per tenant, headed by its Display name, never its Name (owner 2026-10-01).
 */
function groupByTenant(instances: MeInstance[]): Array<[string, MeInstance[]]> {
  const groups = new Map<string, MeInstance[]>();
  for (const instance of instances) {
    const list = groups.get(instance.tenantDisplayName) ?? [];
    list.push(instance);
    groups.set(instance.tenantDisplayName, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/** The line under the heading: loading, the instances across tenants, or what an instance holds. */
function pickerIntro(isLoading: boolean, tenantCount: number, instanceCount: number): string {
  if (isLoading) return "Loading the instances you can reach.";
  if (tenantCount > 1) {
    return `You can reach ${instanceCount} instances across ${tenantCount} tenants. Switch instance any time from the top bar.`;
  }
  return "Connections, schedules, runs and the audit trail all belong to one instance.";
}

/** Nothing to choose: what to do next, the signed-in address, Check again and Sign out. */
function NoInstances({
  administers,
  email,
  onSignOut,
}: {
  /** The user administers the deployment (a platform screen is reachable). */
  administers: boolean;
  email: string | undefined;
  onSignOut: () => void;
}) {
  return (
    <SkyCard className="mt-6 p-6" testId="instances-empty">
      <SkyText as="h2" size={400} weight="semibold" block>
        {administers ? "No instance exists yet" : "No instance has been shared with you yet"}
      </SkyText>
      <SkyText as="p" size={300} color="muted" block className="mt-2">
        {administers
          ? "You administer this deployment. Open platform administration to create a tenant and its first instances."
          : "Ask an administrator to grant you access, then sign in again. Give them the address you signed in with."}
      </SkyText>
      {email ? (
        <SkyText as="p" size={300} block className="mt-3 font-medium" testId="signed-in-email">
          {email}
        </SkyText>
      ) : null}
      <div className="mt-4 flex gap-2">
        <SkyButton
          appearance="secondary"
          icon={RetryIcon}
          testId="instances-check-again"
          onClick={() => window.location.reload()}
        >
          Check again
        </SkyButton>
        <SkyButton
          appearance="subtle"
          icon={SignOutIcon}
          testId="instances-sign-out"
          onClick={onSignOut}
        >
          Sign out
        </SkyButton>
      </div>
    </SkyCard>
  );
}

export default function InstancesPage() {
  const { instances, isLoading, error, selectInstance, selectedInstance, me } = useTenantInstance();
  const { canAny } = usePermissions();
  const { signOut } = useAuth();
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const firstCard = useRef<HTMLDivElement>(null);

  /** The platform screens this user can reach with no instance chosen, or null for nobody else. */
  const platformPath = firstPlatformPath((permission) => canAny(permission));
  // The location that sent the user here (`returnTo`), and the instance its link names when the
  // user can open it there (a notification's run link, PR review behaviour finding 2).
  const { returnTo, linked } = useReturnTo(instances);

  const choose = (instance: MeInstance) => {
    selectInstance(instance.id);
    // The requested location when the chosen instance reaches it; a run link that names another
    // instance then says so on the run page and offers Switch (R12).
    if (returnTo && instanceCanOpen(instance, returnTo)) {
      navigate(addressIn(instance.id, returnTo));
      return;
    }
    // Landed from the CHOSEN instance's own permissions, not from the hook: the
    // hook still reflects the selection this click is replacing — no instance at
    // all on a first choice — so a platform admin would be sent to an admin
    // screen instead of into the instance they just picked.
    const granted = new Set(instance.permissions);
    navigate(
      addressIn(
        instance.id,
        firstAllowedPath((permission) => granted.has(permission)),
      ),
    );
  };

  // A link that names an instance the user can open leaves no choice to make: select it and go
  // back at once. Only with nothing selected, which is the only way the redirect sends a link here.
  useEffect(() => {
    if (!linked || !returnTo || selectedInstance) return;
    selectInstance(linked.id);
    navigate(addressIn(linked.id, returnTo), { replace: true });
  }, [linked, returnTo, selectedInstance, selectInstance, navigate]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return instances;
    return instances.filter((instance) =>
      [instance.name, instance.tenantDisplayName, instance.environmentLabel].some((value) =>
        value?.toLowerCase().includes(needle),
      ),
    );
  }, [instances, query]);

  const tenantCount = new Set(instances.map((instance) => instance.tenantId)).size;
  const groups =
    tenantCount > 1 ? groupByTenant(filtered) : [["", filtered] as [string, MeInstance[]]];

  return (
    <main
      id="main-content"
      className="mx-auto max-w-3xl p-6"
      role="main"
      data-testid="instances-page"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <SkyText as="h1" size={600} weight="semibold" block>
            Choose an instance
          </SkyText>
          <SkyText as="p" size={300} color="muted" block className="mt-1">
            {pickerIntro(isLoading, tenantCount, instances.length)}
          </SkyText>
        </div>
        {platformPath ? (
          <SkyButton
            appearance="primary"
            icon={AdminIcon}
            testId="instances-platform-admin"
            onClick={() => navigate(platformPath)}
          >
            Platform administration
          </SkyButton>
        ) : null}
      </div>

      {error ? (
        <div className="mt-6">
          <SkyMessageBar
            intent="error"
            layout="multiline"
            testId="instances-error"
            title="We could not load your instances"
            message={failureMessage(error, error.message)}
            actions={
              <SkyButton size="small" icon={RetryIcon} onClick={() => window.location.reload()}>
                Try again
              </SkyButton>
            }
          />
        </div>
      ) : null}

      {isLoading ? (
        <div className="mt-6">
          <PickerSkeleton />
        </div>
      ) : null}

      {!isLoading && !error && instances.length === 0 ? (
        <NoInstances
          administers={Boolean(platformPath)}
          email={me?.user.email}
          onSignOut={() => void signOut()}
        />
      ) : null}

      {instances.length > SEARCH_THRESHOLD ? (
        <div className="mt-6">
          <SkyInput
            testId="instance-search"
            aria-label="Search instances and tenants"
            placeholder="Search instances and tenants"
            value={query}
            clearable
            contentBefore={<SearchIcon className="h-4 w-4" />}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery("")}
          />
        </div>
      ) : null}

      {!isLoading && instances.length > 0 && filtered.length === 0 ? (
        <SkyText as="p" size={300} color="muted" block className="mt-6" testId="instances-no-match">
          No instance or tenant matches that search.
        </SkyText>
      ) : null}

      {groups.map(([tenantName, tenantInstances], groupIndex) =>
        tenantInstances.length === 0 ? null : (
          <section key={tenantName || "all"} className="mt-6" aria-label={tenantName || undefined}>
            {tenantName ? (
              // Round 7 T-37: a section heading like every other (it was 12 px muted capitals).
              <SkyText as="h2" size={400} weight="semibold" block className="mb-2">
                {tenantName}
              </SkyText>
            ) : null}
            <ul className="flex flex-col gap-3">
              {tenantInstances.map((instance, index) => (
                <li key={instance.id}>
                  <SkyCard
                    ref={groupIndex === 0 && index === 0 ? firstCard : undefined}
                    appearance="outline"
                    onClick={() => choose(instance)}
                    // The accessible name carries BOTH halves of the identity:
                    // an instance called "prod" exists in every tenant.
                    aria-label={`${instance.tenantDisplayName} ${instance.name}`}
                    testId={`instance-option-${instance.slug}`}
                    className={
                      selectedInstance?.id === instance.id
                        ? "cursor-pointer ring-2 ring-ring"
                        : "cursor-pointer"
                    }
                  >
                    <div className="flex items-center gap-4">
                      <div className="min-w-0 flex-1">
                        <SkyText size={400} weight="semibold" block truncate>
                          {instance.name}
                        </SkyText>
                        <SkyText size={200} color="muted" block truncate>
                          {instance.tenantDisplayName}
                        </SkyText>
                      </div>
                      {instance.environmentLabel ? (
                        <SkyBadge appearance="tint" color="informative">
                          {instance.environmentLabel}
                        </SkyBadge>
                      ) : null}
                      {instance.status === "active" ? null : (
                        // #419: still being set up, or its setup failed and waits for a Retry;
                        // the landing never opens it on its own.
                        <SkyBadge
                          appearance="tint"
                          color={instance.status === "setup_failed" ? "danger" : "warning"}
                          testId={`instance-status-${instance.slug}`}
                        >
                          {instance.status === "setup_failed" ? "Setup failed" : "Provisioning"}
                        </SkyBadge>
                      )}
                      <SkyBadge appearance="outline" color="subtle">
                        {ROLE_LABELS[instance.roleName]}
                      </SkyBadge>
                      <ArrowRightIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </SkyCard>
                </li>
              ))}
            </ul>
          </section>
        ),
      )}
    </main>
  );
}
